import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { language, setLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  // Compact variant for mobile or dashboard header
  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-gray-600 hover:text-cabaza-purple transition-colors rounded-lg hover:bg-gray-100"
          aria-label="Change language"
        >
          <Globe className="w-4 h-4" />
          <span className="uppercase">{currentLanguage.code}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[140px] z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                  lang.code === language ? 'bg-cabaza-purple/5 text-cabaza-purple font-medium' : 'text-gray-700'
                }`}
              >
                <span className="w-5 text-center font-medium text-xs uppercase opacity-60">{lang.flag}</span>
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant with flags
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-cabaza-purple bg-white border border-gray-200 rounded-xl hover:border-cabaza-purple/30 transition-all shadow-sm"
        aria-label="Change language"
      >
        <span className="font-medium text-xs uppercase opacity-70 bg-gray-100 px-1.5 py-0.5 rounded">
          {currentLanguage.flag}
        </span>
        <span>{currentLanguage.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[180px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors ${
                lang.code === language
                  ? 'bg-gradient-to-r from-cabaza-purple/5 to-cabaza-pink/5 text-cabaza-purple font-medium'
                  : 'text-gray-700'
              }`}
            >
              <span className="w-6 text-center font-medium text-xs uppercase bg-gray-100 px-1.5 py-0.5 rounded">
                {lang.flag}
              </span>
              <span>{lang.name}</span>
              {lang.code === language && (
                <span className="ml-auto w-2 h-2 rounded-full bg-cabaza-purple" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
